import "../AccountDetail/AccountDetail.css";
import "./SpotLiveTradeSettings.css";
import { storeUser, updateUser } from "../../../../redux/users/usersSlice";
import { toastify } from "../../../../helpers/toast/toastify";
import { useDispatch, useSelector } from "react-redux";
import React, { useEffect, useState } from "react";
import useApi from "../../../../helpers/apiHelper/requestHelper";
import FloatingSelectField from "../../CommonComponents/FloatingSelectField/FloatingSelectField";
import BubbleSvgButton from "../../CommonComponents/BubbleSvgButton/BubbleSvgButton";
import { fetchUser } from "../../../../helpers/helper";
import { getUserTradePermissionType } from "../../CommonComponents/DashboardMarketSelect/DashboardMarketContext";
import {
  SPOT_DISPLAY_COIN_OPTIONS,
  getSpotDisplayCoin,
  getSpotLiveTradeHideBelow,
  normalizeSpotDisplayCoin,
  persistSpotDisplayCoin,
} from "./spotLiveTradeHideBelow";
import InfoPopup from "../../CommonComponents/InfoPopup/InfoPopup";

const SpotLiveTradeSettings = () => {
  const api = useApi();
  const dispatch = useDispatch();
  const user = useSelector((state) => state?.user?.user);

  const permissionType = getUserTradePermissionType(user);
  const canUseSpot =
    permissionType === "spot" || permissionType === "both";
  const isMember =
    user?.role === "member" ||
    user?.role === "memberNoLink" ||
    Boolean(user?.memberNoLink);

  const [isChanged, setIsChanged] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [savedCoin, setSavedCoin] = useState("USDT");
  const [selectedCoin, setSelectedCoin] = useState("USDT");

  useEffect(() => {
    const nextCoin = getSpotDisplayCoin(user);
    setSavedCoin(nextCoin);
    setSelectedCoin(nextCoin);
  }, [user]);

  useEffect(() => {
    setIsChanged(selectedCoin !== savedCoin);
  }, [selectedCoin, savedCoin]);

  // Leaders/traders only — members inherit their leader's coin
  if (!canUseSpot || isMember) return null;

  const save = async () => {
    setIsLoading(true);
    const hideBelow = Number(getSpotLiveTradeHideBelow(user)) || 0;
    const coin = normalizeSpotDisplayCoin(selectedCoin);

    try {
      const response = await api("post", "users/spotLiveDisplaySettings", {
        spotHideAssetsBelowUsd: hideBelow,
        spotQuotePreference: coin,
      });

      const ok =
        response?.status === true ||
        response?.success === true ||
        response?.status === "success";

      if (ok) {
        persistSpotDisplayCoin(coin);

        if (response?.data?.user) {
          dispatch(updateUser(response.data.user));
        } else if (user) {
          dispatch(
            updateUser({
              ...user,
              spotHideAssetsBelowUsd: hideBelow,
              spotQuotePreference: coin,
            })
          );
        }

        await fetchUser(api, dispatch, storeUser);
        setSavedCoin(coin);
        toastify("Spot live trade settings updated.", "success");
      } else {
        const message =
          (typeof response === "string" && response) ||
          response?.message ||
          response?.error ||
          "Failed to save settings.";
        toastify(message, "error");
      }
    } catch (error) {
      toastify(error?.message || "Failed to save settings.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="accountDetailBox spotLiveTradeSettingsBox">
      <div className="headingBox">
        <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "var(--settingsGaps)" }}>
          <h2 className="title" style={{ marginBottom: 0 }}>Spot Live Trades</h2>
          <InfoPopup
            contentItems={[
              { text: "Spot Live Trades", type: "heading" },
              {
                text: " Configure which coin is displayed for your spot live trades. This setting determines the coin pair shown to subscribers in your live trade feed.",
                type: "paragraph",
              },
            ]}
          />
        </div>
      </div>

      <div className="spotLiveTradeFieldsRow">
        <div
          className={`spotLiveTradeField spotLiveTradeField--single ${
            selectedCoin === savedCoin ? "activeFadeText" : ""
          }`}
        >
          <FloatingSelectField
            id="spotDisplayCoin"
            label="What coin do you use?"
            options={SPOT_DISPLAY_COIN_OPTIONS}
            value={selectedCoin}
            onChange={(e) => setSelectedCoin(e.target.value)}
            focusedAllowed={false}
          />
        </div>
      </div>

      {isChanged && (
        <div className="d-flex justify-content-end align-items-center spotLiveTradeSaveRow">
          <BubbleSvgButton
            className="accountSecurityBtn"
            onClick={save}
            text="Save edits"
            type="button"
            variant="green"
            containerStyling={{
              justifyContent: "center",
              padding: 0,
            }}
            disabled={isLoading}
            isLoading={isLoading}
          />
        </div>
      )}
    </div>
  );
};

export default SpotLiveTradeSettings;
